/**
 * The `audience_artifact` chat card — the audience adapter for the shared {@link ArtifactCard}, which is the
 * one shape every chat artifact renders as (dashboards and saved routines are its siblings). This file
 * supplies only what is audience-specific: the opt-mark tile, the Draft/Active badge, the size line, and
 * the CTA into the audience page.
 *
 * The size is the plain count "8,111 (79.9% of 10,150)", shown only once counted — an uncounted "Unknown"
 * is a dead-end here, since this card has no way to calculate one.
 *
 * Only the CTA navigates; clicking the title or count does nothing. The card is a static panel with one
 * clear action, not a whole-card click target (unlike the dashboard adapter, whose body opens a preview).
 *
 * A card is normally guaranteed a count — `save_audience_draft` counts before the draft exists — but it
 * renders whatever `countState` it is given, so a later re-open after an edit can show `error`/`stale` too.
 *
 * NOTE (iteration 1): the design kit also renders each rule as a value-chip inside the card. The
 * `audience_artifact` ui_message carries only the count metadata (no definition/rules), so rule chips
 * would need a contract change — deferred. Rules DO render on the audience page's definition surface —
 * the read-only visual builder ({@link PredicateTree}), which has the full definition.
 */
import { Users } from 'lucide-react';

import { ArtifactCard } from '@/_shared/components/ArtifactCard';
import { AUDIENCE_MISSING_ID_REASON } from '@/audiences/lib/audience-nav';

import type { CountState } from '../types';
import { AudienceStatusBadge } from './AudienceStatusBadge';
import { CountBadge } from './CountBadge';
import { StaleIndicator } from './StaleIndicator';

export interface AudienceArtifactCardProps {
  title: string;
  status: 'draft' | 'saved';
  countState: CountState;
  /** The definition changed since `countState` was produced — shown alongside the count summary. */
  stale?: boolean;
  /**
   * `compact` is the copy pinned over the top of the transcript once the inline card scrolls past it
   * (ACT2-538). It keeps the first line only, so the size line below is dropped — see `ArtifactCard`.
   */
  variant?: 'default' | 'compact';
  onOpen?: () => void;
  className?: string;
}

export function AudienceArtifactCard({
  title,
  status,
  countState,
  stale = false,
  variant = 'default',
  onOpen,
  className,
}: AudienceArtifactCardProps): React.ReactElement {
  return (
    <ArtifactCard
      icon={<Users />}
      title={title}
      badge={<AudienceStatusBadge status={status} />}
      meta={
        countState.status === 'counted' ? (
          <>
            {/* No icon — the tile already flags this as an audience. */}
            <CountBadge state={countState} icon={null} />
            {stale ? <StaleIndicator /> : null}
          </>
        ) : undefined
      }
      action={
        onOpen
          ? { label: 'Go to audience', onSelect: onOpen }
          : {
              label: 'Go to audience',
              disabledReason: AUDIENCE_MISSING_ID_REASON,
            }
      }
      variant={variant}
      className={className}
    />
  );
}
